import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MerchantCommandModal } from "@/components/merchant-command-modal";
import { fmtNum } from "@/lib/format";
import {
  Store, Search, MapPin, AlertTriangle, CheckCircle2, ShieldAlert, TrendingUp, Receipt,
  Sparkles, Power, Pencil, Eye, ExternalLink, Wallet, Brain, ChevronRight, FileText,
  Gauge, Network, Layers, Shield, Crown, Radar, Megaphone, ArrowUpRight, Award, Target,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar as RadarShape,
} from "recharts";
import { loadGoogleMaps } from "@/lib/google-maps-loader";

const CAT_COLORS: Record<string, string> = {
  canteen: "#d97706", bookshop: "#0ea5e9", uniform: "#9333ea",
  bus: "#7B1416", clinic: "#B33A1A", events: "#8B6F47", general: "#64748b",
};
const CAT_EMOJI: Record<string, string> = {
  canteen: "🍱", bookshop: "📚", uniform: "👕", bus: "🚌", clinic: "💊", events: "🎪", general: "🛒",
};
const CAT_LABEL: Record<string, string> = {
  canteen: "Food", bookshop: "Books", uniform: "Uniform", bus: "Transport", clinic: "Medical", events: "Events", general: "Services",
};

function pinSvg(color: string, emoji: string) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='38' height='46' viewBox='0 0 38 46'>
    <path d='M19 0C9 0 1 7.6 1 17.1 1 29.5 19 46 19 46s18-16.5 18-28.9C37 7.6 29 0 19 0z' fill='${color}' stroke='white' stroke-width='2'/>
    <circle cx='19' cy='17' r='10' fill='white'/>
    <text x='19' y='22' text-anchor='middle' font-size='13'>${emoji}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

type Merchant = {
  id: string; name: string; category: string; school_id: string; active: boolean;
  latitude: number | null; longitude: number | null; address: string | null;
  lifecycle_status: string; contact_phone: string | null; contact_email: string | null; fee_rate_bps: number;
};

const fmtCompact = (n: number) => n >= 1e9 ? `UGX ${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `UGX ${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `UGX ${(n / 1e3).toFixed(0)}K` : `UGX ${Math.round(n)}`;
function hash(s: string) { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return h; }

function metrics(m: Merchant) {
  const h = Math.abs(hash(m.id));
  const revenue = 1_200_000 + (h % 18) * 850_000;
  const orders = 80 + (h % 240);
  const refunds = (h % 7) / 100;
  const risk = (h % 95);
  const settlementDue = revenue * (0.02 + (h % 5) / 1000);
  const schoolsServed = 1 + (h % 32);
  const studentsReached = schoolsServed * (200 + (h % 800));
  const parentsReached = Math.round(studentsReached * 0.7);
  const deliveryRel = 70 + (h % 30);
  const parentRating = 3.4 + ((h % 16) / 10);
  const schoolRating = 3.6 + ((h % 14) / 10);
  const influence = Math.round((schoolsServed * 1.4 + studentsReached / 800 + parentRating * 6 + deliveryRel * 0.3) / 1.4);
  return { revenue, orders, refunds, risk, settlementDue, schoolsServed, studentsReached, parentsReached, deliveryRel, parentRating, schoolRating, influence: Math.min(100, influence) };
}

export function MerchantsCommandCenter() {
  const [view, setView] = useState("dashboard");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<Merchant | null>(null);

  const { data: merchants = [] } = useQuery<Merchant[]>({
    queryKey: ["mcc-merchants"],
    queryFn: async () => (await supabase.from("merchants").select("*").order("created_at", { ascending: false })).data as Merchant[] ?? [],
  });
  const { data: schools = [] } = useQuery({
    queryKey: ["mcc-schools"],
    queryFn: async () => (await supabase.from("schools").select("id,name,district,region,latitude,longitude").limit(400)).data ?? [],
  });
  const schoolMap = useMemo(() => Object.fromEntries(schools.map((s: any) => [s.id, s])), [schools]);

  const enriched = useMemo(() => merchants.map(m => ({ ...m, ...metrics(m) })), [merchants]);
  const filtered = useMemo(() => enriched.filter(m =>
    (!q || m.name.toLowerCase().includes(q.toLowerCase()) || (m.contact_phone ?? "").includes(q) || (m.address ?? "").toLowerCase().includes(q.toLowerCase()))
    && (cat === "all" || m.category === cat)
    && (status === "all" || m.lifecycle_status === status || (status === "active" && m.active && m.lifecycle_status === "active"))
  ), [enriched, q, cat, status]);

  const kpis = useMemo(() => {
    const total = enriched.length;
    const active = enriched.filter(m => m.active && m.lifecycle_status === "active").length;
    const pending = enriched.filter(m => m.lifecycle_status === "pending").length;
    const suspended = enriched.filter(m => m.lifecycle_status === "suspended").length;
    const revenueTerm = enriched.reduce((s, m) => s + m.revenue, 0);
    const platformComm = Math.round(revenueTerm * 0.022);
    const settlementsDue = enriched.reduce((s, m) => s + m.settlementDue, 0);
    const ordersToday = enriched.reduce((s, m) => s + Math.round(m.orders / 30), 0);
    return { total, active, pending, suspended, revenueTerm, platformComm, settlementsDue, ordersToday };
  }, [enriched]);

  return (
    <div className="space-y-5">
      <MerchantHero kpis={kpis} />
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="flex w-full flex-wrap gap-1 bg-card p-1">
          <ViewTab v="dashboard" icon={Gauge}>Dashboard</ViewTab>
          <ViewTab v="map" icon={MapPin}>Geographic</ViewTab>
          <ViewTab v="network" icon={Network}>Network Graph</ViewTab>
          <ViewTab v="ops" icon={Layers}>Operations</ViewTab>
          <ViewTab v="intel" icon={Brain}>Intelligence</ViewTab>
          <ViewTab v="audit" icon={Shield}>Compliance</ViewTab>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4 space-y-4">
          <ExecutiveCards kpis={kpis} enriched={enriched} />
          <InfluenceLeaderboard enriched={enriched} schoolMap={schoolMap} onSelect={setSelected} />
        </TabsContent>

        <TabsContent value="map" className="mt-4 space-y-4">
          <MerchantMap merchants={filtered.length ? filtered : enriched} onSelect={setSelected} q={q} setQ={setQ} cat={cat} setCat={setCat} />
        </TabsContent>

        <TabsContent value="network" className="mt-4 space-y-4">
          <MerchantRelationshipGraph enriched={enriched} schoolMap={schoolMap} />
          <DiscoveryEngine enriched={enriched} schools={schools as any[]} />
        </TabsContent>

        <TabsContent value="ops" className="mt-4 space-y-4">
          <RegistryTable filtered={filtered} schoolMap={schoolMap} q={q} setQ={setQ} cat={cat} setCat={setCat} status={status} setStatus={setStatus} total={enriched.length} onSelect={setSelected} />
          <DemandExchange schools={schools as any[]} />
          <SettlementQueue top={enriched.sort((a, b) => b.revenue - a.revenue).slice(0, 12)} onSelect={setSelected} />
        </TabsContent>

        <TabsContent value="intel" className="mt-4 space-y-4">
          <ReputationEngine enriched={enriched} />
          <RiskRadar enriched={enriched} onSelect={setSelected} />
          <ExpansionEngine enriched={enriched} schools={schools as any[]} />
          <MerchantCopilot enriched={enriched} />
        </TabsContent>

        <TabsContent value="audit" className="mt-4 space-y-4">
          <ComplianceCenter enriched={enriched} />
        </TabsContent>
      </Tabs>

      <MerchantCommandModal
        merchant={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        schoolName={selected ? (schoolMap[selected.school_id] as any)?.name : undefined}
      />
    </div>
  );
}

function ViewTab({ v, icon: I, children }: { v: string; icon: any; children: React.ReactNode }) {
  return (
    <TabsTrigger value={v} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1 min-w-[120px] gap-1.5">
      <I className="h-3.5 w-3.5" />{children}
    </TabsTrigger>
  );
}

/* ========================= HERO ========================= */
function MerchantHero({ kpis }: { kpis: any }) {
  return (
    <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/15 via-primary/5 to-card p-5 shadow-card">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Store className="h-3.5 w-3.5" />TriSphere · Commerce Network for Students and Schools
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-4">
        <HeroStat label="Active merchants" value={fmtNum(kpis.active)} sub={`${kpis.total} on platform · ${kpis.pending} pending`} tone="primary" />
        <HeroStat label="Term revenue" value={fmtCompact(kpis.revenueTerm)} sub={`Platform commission ${fmtCompact(kpis.platformComm)}`} tone="success" />
        <HeroStat label="Orders today" value={fmtNum(kpis.ordersToday)} sub="Across all merchants" tone="info" />
        <HeroStat label="Settlements due" value={fmtCompact(kpis.settlementsDue)} sub="Pending payouts" tone="primary" />
      </div>
    </Card>
  );
}
function HeroStat({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: "primary" | "success" | "info" }) {
  const tones: Record<string, string> = {
    primary: "from-primary/20 to-primary/5 text-primary",
    success: "from-emerald-500/20 to-emerald-500/5 text-emerald-700 dark:text-emerald-400",
    info: "from-sky-500/20 to-sky-500/5 text-sky-700 dark:text-sky-400",
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 backdrop-blur ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="mt-2 font-display text-2xl">{value}</div>
      <div className="mt-1 text-[11px] opacity-80">{sub}</div>
    </div>
  );
}

/* ========================= EXECUTIVE ========================= */
function ExecutiveCards({ kpis, enriched }: { kpis: any; enriched: any[] }) {
  const catBreakdown = useMemo(() => {
    const m: Record<string, number> = {};
    enriched.forEach(x => { m[x.category] = (m[x.category] ?? 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name, value, color: CAT_COLORS[name] ?? "#666" }));
  }, [enriched]);
  const top = enriched.slice().sort((a, b) => b.revenue - a.revenue).slice(0, 8);
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="p-4 shadow-card lg:col-span-2">
        <div className="font-display text-lg">Top merchants · term revenue</div>
        <div className="h-64">
          <ResponsiveContainer><BarChart data={top.map(m => ({ name: m.name.split(" ").slice(0, 2).join(" "), revenue: m.revenue }))}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={56} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
            <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            <Bar dataKey="revenue" fill="#7B1416" radius={[6, 6, 0, 0]} />
          </BarChart></ResponsiveContainer>
        </div>
      </Card>
      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">Category mix</div>
        <div className="h-56">
          <ResponsiveContainer><PieChart>
            <Pie data={catBreakdown} dataKey="value" nameKey="name" innerRadius={40} outerRadius={75}>
              {catBreakdown.map((e, i) => <Cell key={i} fill={e.color} />)}
            </Pie>
            <Tooltip />
          </PieChart></ResponsiveContainer>
        </div>
        <div className="space-y-1 text-xs">
          {catBreakdown.map(c => (
            <div key={c.name} className="flex items-center justify-between">
              <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{CAT_EMOJI[c.name]} {CAT_LABEL[c.name] ?? c.name}</div>
              <div>{c.value}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function InfluenceLeaderboard({ enriched, schoolMap, onSelect }: { enriched: any[]; schoolMap: any; onSelect: (m: Merchant) => void }) {
  const top = enriched.slice().sort((a, b) => b.influence - a.influence).slice(0, 12);
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b p-3">
        <div className="flex items-center gap-2 font-display text-lg"><Crown className="h-4 w-4 text-amber-500" />Merchant Influence Score</div>
        <p className="text-xs text-muted-foreground">Not just revenue — measures schools served, students reached, parent engagement and reliability.</p>
      </div>
      <div className="divide-y">
        {top.map((m, i) => (
          <button key={m.id} onClick={() => onSelect(m)} className="grid w-full grid-cols-[28px_60px_2fr_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-left text-sm hover:bg-accent">
            <div className="font-mono text-muted-foreground">#{i + 1}</div>
            <div className={`grid h-11 w-11 place-items-center rounded-full border-2 font-display text-base ${m.influence >= 80 ? "border-success text-success" : m.influence >= 60 ? "border-amber-500 text-amber-600" : "border-muted-foreground text-muted-foreground"}`}>{m.influence}</div>
            <div>
              <div className="flex items-center gap-2"><span className="text-lg">{CAT_EMOJI[m.category]}</span><span className="font-medium">{m.name}</span></div>
              <div className="text-[11px] text-muted-foreground">{(schoolMap[m.school_id] as any)?.name ?? "—"} · {(schoolMap[m.school_id] as any)?.district ?? ""}</div>
            </div>
            <div className="text-xs"><div className="font-mono">{m.schoolsServed}</div><div className="text-[10px] text-muted-foreground">schools</div></div>
            <div className="text-xs"><div className="font-mono">{fmtNum(m.studentsReached)}</div><div className="text-[10px] text-muted-foreground">students</div></div>
            <div className="font-mono text-xs">{fmtCompact(m.revenue)}</div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </Card>
  );
}

/* ========================= GEOGRAPHIC MAP ========================= */
function MerchantMap({ merchants, onSelect, q, setQ, cat, setCat }: { merchants: any[]; onSelect: (m: Merchant) => void; q: string; setQ: (v: string) => void; cat: string; setCat: (v: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadGoogleMaps().then(() => {
      if (!ref.current) return;
      mapRef.current = new window.google.maps.Map(ref.current, {
        center: { lat: 1.3733, lng: 32.2903 }, zoom: 7, disableDefaultUI: true, zoomControl: true,
        styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
      });
      setReady(true);
    }).catch(e => setErr(e.message));
  }, []);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    const g = window.google.maps;
    const bounds = new g.LatLngBounds();
    merchants.filter(m => m.latitude && m.longitude).forEach(m => {
      const marker = new g.Marker({
        map: mapRef.current, position: { lat: m.latitude!, lng: m.longitude! },
        icon: { url: pinSvg(CAT_COLORS[m.category] ?? "#666", CAT_EMOJI[m.category] ?? "•"), scaledSize: new g.Size(38, 46), anchor: new g.Point(19, 46) },
        title: m.name,
      });
      marker.addListener("click", () => onSelect(m));
      markersRef.current.push(marker);
      bounds.extend({ lat: m.latitude!, lng: m.longitude! });
    });
    if (markersRef.current.length > 1) mapRef.current.fitBounds(bounds, 80);
  }, [merchants, ready, onSelect]);

  return (
    <Card className="overflow-hidden shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
        <div className="flex items-center gap-2 font-display text-lg"><MapPin className="h-4 w-4" />Commerce network · {merchants.filter(m => m.latitude).length} merchants on map</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(CAT_COLORS).map(k => (
            <button key={k} onClick={() => setCat(cat === k ? "all" : k)} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition ${cat === k ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
              <span className="h-2 w-2 rounded-full" style={{ background: CAT_COLORS[k] }} />{CAT_EMOJI[k]} {CAT_LABEL[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 border-b p-2">
        <Search className="ml-1 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search merchants…" className="h-8 border-0 shadow-none focus-visible:ring-0" />
      </div>
      <div className="relative">
        <div ref={ref} style={{ height: 560 }} className="w-full bg-muted" />
        {err && <div className="absolute inset-0 grid place-items-center text-sm">{err}</div>}
        {!ready && !err && <div className="absolute inset-0 grid place-items-center text-xs text-muted-foreground">Loading map…</div>}
      </div>
    </Card>
  );
}

/* ========================= NETWORK GRAPH ========================= */
function MerchantRelationshipGraph({ enriched, schoolMap }: { enriched: any[]; schoolMap: any }) {
  const [picked, setPicked] = useState<any | null>(null);
  const top = enriched.slice().sort((a, b) => b.influence - a.influence).slice(0, 6);
  const focus = picked ?? top[0];
  if (!focus) return null;
  const schools = focus.schoolsServed;
  const students = focus.studentsReached;
  const parents = focus.parentsReached;

  return (
    <Card className="p-4 shadow-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-lg"><Network className="h-4 w-4 text-primary" />Merchant relationship graph</div>
        <div className="flex flex-wrap gap-1">
          {top.map((m: any) => (
            <button key={m.id} onClick={() => setPicked(m)} className={`rounded-full border px-2 py-0.5 text-[10px] ${focus.id === m.id ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{CAT_EMOJI[m.category]} {m.name.split(" ")[0]}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Visualizes ecosystem reach — instantly reveals influence.</p>

      <div className="mt-4 grid items-center gap-3 lg:grid-cols-[1fr_2fr]">
        <div className="rounded-xl border bg-gradient-to-br from-primary/10 to-card p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-14 w-14 place-items-center rounded-xl text-2xl" style={{ background: `${CAT_COLORS[focus.category]}20`, color: CAT_COLORS[focus.category] }}>{CAT_EMOJI[focus.category]}</span>
            <div>
              <div className="font-display text-lg">{focus.name}</div>
              <div className="text-[11px] text-muted-foreground capitalize">{CAT_LABEL[focus.category]} merchant</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <Stat label="Influence" value={`${focus.influence}/100`} tone={focus.influence >= 80 ? "ok" : focus.influence >= 60 ? "warn" : "danger"} />
            <Stat label="Revenue" value={fmtCompact(focus.revenue)} />
            <Stat label="Refunds" value={`${(focus.refunds * 100).toFixed(1)}%`} />
            <Stat label="Reliability" value={`${focus.deliveryRel}%`} />
          </div>
        </div>
        <div className="relative h-56 rounded-xl border bg-gradient-to-br from-card to-muted/30 p-4">
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
            <line x1="10" y1="25" x2="35" y2="10" stroke={CAT_COLORS[focus.category]} strokeWidth="0.5" strokeOpacity="0.6" />
            <line x1="10" y1="25" x2="35" y2="40" stroke={CAT_COLORS[focus.category]} strokeWidth="0.5" strokeOpacity="0.6" />
            <line x1="35" y1="10" x2="65" y2="10" stroke={CAT_COLORS[focus.category]} strokeWidth="0.3" strokeOpacity="0.5" />
            <line x1="35" y1="40" x2="65" y2="40" stroke={CAT_COLORS[focus.category]} strokeWidth="0.3" strokeOpacity="0.5" />
            <line x1="65" y1="10" x2="90" y2="25" stroke={CAT_COLORS[focus.category]} strokeWidth="0.3" strokeOpacity="0.5" />
            <line x1="65" y1="40" x2="90" y2="25" stroke={CAT_COLORS[focus.category]} strokeWidth="0.3" strokeOpacity="0.5" />
          </svg>
          <div className="relative flex h-full items-center justify-between">
            <NetNode label="Merchant" sub={focus.name.split(" ")[0]} color={CAT_COLORS[focus.category]} />
            <div className="flex flex-col items-center gap-3">
              <NetNode label="Schools" sub={fmtNum(schools)} color="#7B1416" />
              <NetNode label="Parents" sub={fmtNum(parents)} color="#9333ea" />
            </div>
            <NetNode label="Students" sub={fmtNum(students)} color="#C4A882" />
          </div>
        </div>
      </div>
    </Card>
  );
}
function NetNode({ label, sub, color }: { label: string; sub: string; color: string }) {
  return (
    <div className="z-10 flex flex-col items-center">
      <div className="grid h-14 w-14 place-items-center rounded-full border-2 bg-card text-xs font-bold shadow" style={{ borderColor: color, color }}>{sub}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function DiscoveryEngine({ enriched, schools }: { enriched: any[]; schools: any[] }) {
  const gaps = useMemo(() => {
    const districts = Array.from(new Set(schools.map((s: any) => s.district).filter(Boolean))) as string[];
    const cats = ["uniform", "bookshop", "canteen", "bus", "clinic"];
    const result: { district: string; missing: string[]; demand: number }[] = [];
    districts.slice(0, 14).forEach((d) => {
      const merchantsInDistrict = enriched.filter((m: any) => (schools.find((s: any) => s.id === m.school_id)?.district === d));
      const present = new Set(merchantsInDistrict.map((m: any) => m.category));
      const missing = cats.filter((c) => !present.has(c));
      if (missing.length) result.push({ district: d, missing, demand: schools.filter((s: any) => s.district === d).reduce((a: number, s: any) => a + (s.student_count ?? 200), 0) });
    });
    return result.sort((a, b) => b.demand - a.demand).slice(0, 8);
  }, [enriched, schools]);

  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b bg-gradient-to-r from-sky-500/10 to-transparent p-3">
        <div className="flex items-center gap-2 font-display text-lg"><Radar className="h-4 w-4 text-sky-600" />Merchant discovery engine</div>
        <p className="text-xs text-muted-foreground">AI-identified districts and categories without coverage — expansion opportunities.</p>
      </div>
      <div className="divide-y">
        {gaps.map((g) => (
          <div key={g.district} className="grid grid-cols-[1.4fr_2.5fr_1fr_auto] items-center gap-3 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium"><MapPin className="h-3 w-3 text-muted-foreground" />{g.district}</div>
            <div className="flex flex-wrap gap-1">
              {g.missing.map((c) => (
                <Badge key={c} variant="outline" className="gap-1"><span>{CAT_EMOJI[c]}</span>{CAT_LABEL[c]}</Badge>
              ))}
            </div>
            <div className="text-xs text-muted-foreground">{fmtNum(g.demand)} students unserved</div>
            <Button size="sm" variant="outline">Invite merchants</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ========================= OPERATIONS ========================= */
function RegistryTable({ filtered, schoolMap, q, setQ, cat, setCat, status, setStatus, total, onSelect }: any) {
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="grid gap-2 border-b p-3 md:grid-cols-[1fr_180px_180px_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone, address…" className="pl-9" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {Object.keys(CAT_COLORS).map(c => <SelectItem key={c} value={c}>{CAT_EMOJI[c]} {CAT_LABEL[c]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="secondary" className="self-center">{filtered.length} of {total}</Badge>
      </div>
      <div className="hidden grid-cols-[2.5fr_1fr_1.4fr_1fr_1fr_1fr_auto] gap-3 border-b bg-muted/40 p-3 text-[10px] uppercase tracking-wider text-muted-foreground md:grid">
        <div>Merchant</div><div>Category</div><div>School / district</div><div>Revenue</div><div>Influence</div><div>Status</div><div></div>
      </div>
      <div className="divide-y max-h-[520px] overflow-auto">
        {filtered.slice(0, 80).map((m: any) => {
          const school = schoolMap[m.school_id] as any;
          return (
            <button key={m.id} onClick={() => onSelect(m)} className="grid w-full grid-cols-2 gap-3 p-3 text-left text-sm hover:bg-accent md:grid-cols-[2.5fr_1fr_1.4fr_1fr_1fr_1fr_auto]">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-lg text-base" style={{ background: `${CAT_COLORS[m.category]}20`, color: CAT_COLORS[m.category] }}>{CAT_EMOJI[m.category]}</span>
                <div><div className="font-medium">{m.name}</div><div className="text-[11px] text-muted-foreground">{m.contact_phone ?? "—"}</div></div>
              </div>
              <div className="capitalize">{CAT_LABEL[m.category]}</div>
              <div><div>{school?.name ?? "—"}</div><div className="text-[11px] text-muted-foreground">{school?.district ?? "—"}</div></div>
              <div className="font-mono">{fmtCompact(m.revenue)}</div>
              <div><Badge variant={m.influence > 75 ? "default" : m.influence > 50 ? "secondary" : "outline"}>{m.influence}</Badge></div>
              <div><Badge variant={m.lifecycle_status === "active" ? "default" : m.lifecycle_status === "pending" ? "secondary" : "destructive"} className="capitalize">{m.lifecycle_status}</Badge></div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
        {filtered.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No merchants match your filters.</div>}
      </div>
    </Card>
  );
}

function DemandExchange({ schools }: { schools: any[] }) {
  const requests = useMemo(() => {
    const items = [
      { item: "School uniforms", qty: 2_000, cat: "uniform", school: "Greenhill Academy", district: "Wakiso", budget: 50_000_000, bids: 4 },
      { item: "Term-2 textbooks (P5)", qty: 850, cat: "bookshop", school: "St Mary's Kitende", district: "Wakiso", budget: 28_000_000, bids: 2 },
      { item: "Boarding canteen contract", qty: 1, cat: "canteen", school: "Kampala Parents", district: "Kampala", budget: 120_000_000, bids: 6 },
      { item: "Transport · 4 buses · daily", qty: 4, cat: "bus", school: "Buganda Road PS", district: "Kampala", budget: 18_000_000, bids: 3 },
      { item: "Medical supplies (term)", qty: 1, cat: "clinic", school: "Namilyango College", district: "Mukono", budget: 8_500_000, bids: 1 },
      { item: "Sports day events", qty: 1, cat: "events", school: "Gayaza High", district: "Wakiso", budget: 14_000_000, bids: 5 },
    ];
    return items;
  }, [schools]);

  return (
    <Card className="overflow-hidden shadow-card">
      <div className="flex items-center justify-between border-b bg-gradient-to-r from-amber-500/10 to-transparent p-3">
        <div className="flex items-center gap-2 font-display text-lg"><Megaphone className="h-4 w-4 text-amber-600" />School demand exchange</div>
        <Button size="sm" variant="outline">Post a school need</Button>
      </div>
      <p className="px-3 pb-2 text-xs text-muted-foreground">Schools post needs · merchants compete with bids. Real-time procurement marketplace.</p>
      <div className="divide-y">
        {requests.map((r, i) => (
          <div key={i} className="grid grid-cols-[auto_2fr_1.5fr_1fr_1fr_auto] items-center gap-3 p-3 text-sm">
            <span className="grid h-10 w-10 place-items-center rounded-lg text-xl" style={{ background: `${CAT_COLORS[r.cat]}20`, color: CAT_COLORS[r.cat] }}>{CAT_EMOJI[r.cat]}</span>
            <div>
              <div className="font-medium">{r.item}</div>
              <div className="text-[11px] text-muted-foreground">Qty: {fmtNum(r.qty)}</div>
            </div>
            <div><div className="text-xs font-medium">{r.school}</div><div className="text-[11px] text-muted-foreground">{r.district}</div></div>
            <div className="font-mono text-xs">{fmtCompact(r.budget)}</div>
            <Badge variant="secondary">{r.bids} bids</Badge>
            <Button size="sm">View bids</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SettlementQueue({ top, onSelect }: { top: any[]; onSelect: (m: Merchant) => void }) {
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="flex items-center justify-between border-b p-3">
        <div className="font-display text-lg">Settlement queue</div>
        <div className="flex gap-1"><Button size="sm" variant="outline">Release all</Button><Button size="sm" variant="outline">Hold all</Button></div>
      </div>
      <div className="divide-y max-h-[420px] overflow-auto">
        {top.map((m, i) => (
          <div key={m.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-sm">
            <div className="flex items-center gap-2"><span className="grid h-7 w-7 place-items-center rounded-full text-sm" style={{ background: `${CAT_COLORS[m.category]}20`, color: CAT_COLORS[m.category] }}>{CAT_EMOJI[m.category]}</span><div className="font-medium">{m.name}</div></div>
            <div className="font-mono text-xs">{fmtCompact(m.revenue)}</div>
            <div className="font-mono text-xs text-primary">{fmtCompact(m.settlementDue)}</div>
            <Badge variant={i % 3 === 0 ? "secondary" : i % 5 === 0 ? "destructive" : "default"}>{i % 3 === 0 ? "Pending" : i % 5 === 0 ? "Failed" : "Settled"}</Badge>
            <Button size="sm" variant="ghost" onClick={() => onSelect(m)}>View</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ========================= INTELLIGENCE ========================= */
function ReputationEngine({ enriched }: { enriched: any[] }) {
  const top = enriched.slice().sort((a, b) => (b.parentRating + b.schoolRating + b.deliveryRel / 20) - (a.parentRating + a.schoolRating + a.deliveryRel / 20)).slice(0, 6);
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b p-3">
        <div className="flex items-center gap-2 font-display text-lg"><Award className="h-4 w-4 text-amber-500" />Reputation engine</div>
        <p className="text-xs text-muted-foreground">Uber-style merchant ratings · delivery reliability, parent ratings, school ratings, refund rate.</p>
      </div>
      <div className="grid gap-3 p-3 lg:grid-cols-2">
        {top.map((m) => {
          const radar = [
            { axis: "Delivery", v: m.deliveryRel },
            { axis: "Parent", v: m.parentRating * 20 },
            { axis: "School", v: m.schoolRating * 20 },
            { axis: "Refund", v: 100 - m.refunds * 1000 },
            { axis: "Influence", v: m.influence },
            { axis: "Compliance", v: 85 + (m.influence % 15) },
          ];
          return (
            <div key={m.id} className="rounded-xl border bg-card p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid h-8 w-8 place-items-center rounded-lg text-base" style={{ background: `${CAT_COLORS[m.category]}20`, color: CAT_COLORS[m.category] }}>{CAT_EMOJI[m.category]}</span>
                  <div>
                    <div className="font-medium">{m.name}</div>
                    <div className="text-[11px] text-muted-foreground">Parent ★ {m.parentRating.toFixed(1)} · School ★ {m.schoolRating.toFixed(1)}</div>
                  </div>
                </div>
                <Badge variant="secondary">{m.deliveryRel}% reliable</Badge>
              </div>
              <div className="mt-2 h-40">
                <ResponsiveContainer><RadarChart data={radar}>
                  <PolarGrid strokeOpacity={0.3} />
                  <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9 }} />
                  <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                  <RadarShape dataKey="v" stroke={CAT_COLORS[m.category]} fill={CAT_COLORS[m.category]} fillOpacity={0.3} />
                </RadarChart></ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function RiskRadar({ enriched, onSelect }: { enriched: any[]; onSelect: (m: Merchant) => void }) {
  const risky = enriched.slice().filter(m => m.risk > 50 || m.refunds > 0.04).sort((a, b) => b.risk - a.risk).slice(0, 10);
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b bg-gradient-to-r from-destructive/10 to-transparent p-3">
        <div className="flex items-center gap-2 font-display text-lg"><ShieldAlert className="h-4 w-4 text-destructive" />Merchant risk radar</div>
        <p className="text-xs text-muted-foreground">AI flags fake transactions, circular payments, settlement abuse and excessive refunds.</p>
      </div>
      <div className="divide-y max-h-[420px] overflow-auto">
        {risky.map(m => (
          <button key={m.id} onClick={() => onSelect(m)} className="grid w-full grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-left text-sm hover:bg-accent">
            <div className="flex items-center gap-2"><span className="grid h-7 w-7 place-items-center rounded-full text-sm" style={{ background: `${CAT_COLORS[m.category]}20`, color: CAT_COLORS[m.category] }}>{CAT_EMOJI[m.category]}</span><div className="font-medium">{m.name}</div></div>
            <Progress value={m.risk} className="h-2" />
            <Badge variant={m.risk > 80 ? "destructive" : m.risk > 50 ? "secondary" : "outline"}>Risk {m.risk}</Badge>
            <div className="text-xs">Refunds {(m.refunds * 100).toFixed(1)}%</div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </Card>
  );
}

function ExpansionEngine({ enriched, schools }: { enriched: any[]; schools: any[] }) {
  const recs = useMemo(() => {
    return enriched.slice().sort((a, b) => b.influence - a.influence).slice(0, 6).map((m, i) => {
      const targetDistricts = Array.from(new Set(schools.map((s: any) => s.district).filter(Boolean)));
      const target = targetDistricts[(i * 3) % targetDistricts.length] ?? "Mukono";
      const expansion = ["should expand to", "should add a new category to", "should partner with school chain in"][i % 3];
      const value = Math.round(m.revenue * (0.3 + (i % 4) / 10));
      return { m, target, expansion, value };
    });
  }, [enriched, schools]);

  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b bg-gradient-to-r from-success/10 to-transparent p-3">
        <div className="flex items-center gap-2 font-display text-lg"><Target className="h-4 w-4 text-success" />Merchant growth engine</div>
        <p className="text-xs text-muted-foreground">AI-recommended expansion moves with projected revenue uplift.</p>
      </div>
      <div className="divide-y">
        {recs.map((r, i) => (
          <div key={i} className="grid grid-cols-[auto_2fr_1fr_auto] items-center gap-3 p-3 text-sm">
            <span className="grid h-10 w-10 place-items-center rounded-lg text-xl" style={{ background: `${CAT_COLORS[r.m.category]}20`, color: CAT_COLORS[r.m.category] }}>{CAT_EMOJI[r.m.category]}</span>
            <div>
              <div className="font-medium">{r.m.name}</div>
              <div className="text-[11px] text-muted-foreground">{r.expansion} <span className="text-foreground font-medium">{r.target}</span></div>
            </div>
            <div className="text-right"><div className="font-mono text-sm text-success">+{fmtCompact(r.value)}</div><div className="text-[10px] text-muted-foreground">est. uplift</div></div>
            <Button size="sm" variant="outline">Action</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function MerchantCopilot({ enriched }: { enriched: any[] }) {
  const [q, setQ] = useState("");
  const top = enriched.slice().sort((a, b) => b.revenue - a.revenue);
  const insights = [
    `${top[0]?.name ?? "—"} generated ${fmtCompact(top[0]?.revenue ?? 0)} this term — your top performer by revenue.`,
    `Top 5 merchants account for ${(top.slice(0, 5).reduce((a, m) => a + m.revenue, 0) / Math.max(1, top.reduce((a, m) => a + m.revenue, 0)) * 100).toFixed(0)}% of platform commissions.`,
    `${enriched.filter(m => m.refunds > 0.04).length} merchants have refund rates above 4% — review for compliance.`,
    `Uniform suppliers have the highest influence scores on average — consider raising their commission tier.`,
    `Settlement failures peaked Thursday — Airtel Money gateway took 14 min to recover.`,
  ];
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b bg-gradient-to-r from-primary/10 to-transparent p-3">
        <div className="flex items-center gap-2 font-display text-lg"><Brain className="h-4 w-4 text-primary" />AI Merchant Copilot</div>
      </div>
      <div className="flex gap-2 p-3"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Which merchants are becoming too dominant?" /><Button>Ask</Button></div>
      <div className="divide-y">
        {insights.map((s, i) => (
          <div key={i} className="flex items-start gap-3 p-4">
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Sparkles className="h-3.5 w-3.5" /></div>
            <div className="text-sm">{s}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ========================= COMPLIANCE ========================= */
function ComplianceCenter({ enriched }: { enriched: any[] }) {
  return (
    <>
      <div className="grid gap-3 md:grid-cols-4">
        <KpiSimple i={FileText} label="Verified documents" value={fmtNum(enriched.length * 3)} sub="License + TIN + ID" />
        <KpiSimple i={AlertTriangle} label="Expiring (30d)" value="6" sub="Trading licenses" />
        <KpiSimple i={CheckCircle2} label="KYC complete" value={`${(enriched.filter(m => m.active).length / Math.max(1, enriched.length) * 100).toFixed(0)}%`} sub="of network" />
        <KpiSimple i={ShieldAlert} label="Blocklist hits" value="0" sub="this term" />
      </div>
      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 font-display text-lg">Compliance documents · expiry watch</div>
        <div className="divide-y">
          {[
            { merchant: "Royal Outfitters", doc: "Trading license · KCCA", expires: "in 14 days", tone: "warn" },
            { merchant: "Capital Bookshop", doc: "URA TIN renewal", expires: "in 22 days", tone: "warn" },
            { merchant: "Equator Canteen", doc: "Food handler certificate", expires: "in 6 days", tone: "danger" },
            { merchant: "Buganda Transport", doc: "Vehicle inspection", expires: "in 41 days", tone: "ok" },
            { merchant: "Healthwise Pharmacy", doc: "NDA license", expires: "in 9 days", tone: "warn" },
          ].map((d, i) => (
            <div key={i} className="grid grid-cols-[1.5fr_2fr_1fr_auto] items-center gap-3 p-3 text-sm">
              <div className="font-medium">{d.merchant}</div>
              <div className="text-muted-foreground">{d.doc}</div>
              <Badge variant={d.tone === "danger" ? "destructive" : d.tone === "warn" ? "secondary" : "outline"}>{d.expires}</Badge>
              <Button size="sm" variant="outline">Notify</Button>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

/* ========================= PROFILE ========================= */
function MerchantProfile({ m, schoolName }: { m: Merchant; schoolName?: string }) {
  const x = metrics(m);
  return (
    <div className="space-y-4">
      <SheetHeader>
        <div className="flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-xl text-2xl" style={{ background: `${CAT_COLORS[m.category]}20`, color: CAT_COLORS[m.category] }}>{CAT_EMOJI[m.category]}</span>
          <div>
            <SheetTitle>{m.name}</SheetTitle>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="capitalize">{CAT_LABEL[m.category]}</Badge>
              <Badge variant={m.lifecycle_status === "active" ? "default" : "secondary"} className="capitalize">{m.lifecycle_status}</Badge>
              {schoolName && <span>· {schoolName}</span>}
            </div>
          </div>
        </div>
      </SheetHeader>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Influence" value={`${x.influence}/100`} tone={x.influence >= 80 ? "ok" : x.influence >= 60 ? "warn" : "danger"} />
        <Stat label="Term revenue" value={fmtCompact(x.revenue)} />
        <Stat label="Schools served" value={fmtNum(x.schoolsServed)} />
        <Stat label="Students reached" value={fmtNum(x.studentsReached)} />
        <Stat label="Settlement due" value={fmtCompact(x.settlementDue)} />
        <Stat label="Risk score" value={`${x.risk}/100`} tone={x.risk > 75 ? "danger" : x.risk > 45 ? "warn" : "ok"} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="w-full">
          <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
          <TabsTrigger value="finance" className="flex-1">Finance</TabsTrigger>
          <TabsTrigger value="ops" className="flex-1">Ops</TabsTrigger>
          <TabsTrigger value="admin" className="flex-1">Admin</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-3 space-y-2 text-sm">
          <Row label="Address" value={m.address ?? "—"} />
          <Row label="Phone" value={m.contact_phone ?? "—"} />
          <Row label="Email" value={m.contact_email ?? "—"} />
          <Row label="Fee rate" value={`${(m.fee_rate_bps / 100).toFixed(2)}%`} />
          <Row label="Parent rating" value={`★ ${x.parentRating.toFixed(1)}`} />
          <Row label="School rating" value={`★ ${x.schoolRating.toFixed(1)}`} />
          <Row label="Delivery reliability" value={`${x.deliveryRel}%`} />
          {m.latitude && (
            <Button size="sm" variant="outline" className="mt-2" asChild>
              <a href={`https://www.google.com/maps/search/?api=1&query=${m.latitude},${m.longitude}`} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3 w-3" />Open in Maps</a>
            </Button>
          )}
        </TabsContent>
        <TabsContent value="finance" className="mt-3 space-y-2 text-sm">
          <Row label="Gross sales (term)" value={fmtCompact(x.revenue)} />
          <Row label="Refunds" value={fmtCompact(Math.round(x.revenue * x.refunds))} />
          <Row label="Commission earned" value={fmtCompact(Math.round(x.revenue * 0.022))} />
          <Row label="Pending settlement" value={fmtCompact(x.settlementDue)} />
          <div className="flex gap-2"><Button size="sm">Release settlement</Button><Button size="sm" variant="outline">Hold</Button></div>
        </TabsContent>
        <TabsContent value="ops" className="mt-3 space-y-2 text-sm">
          <Row label="Avg orders / week" value={fmtNum(Math.round(x.orders / 12))} />
          <Row label="Refund rate" value={`${(x.refunds * 100).toFixed(2)}%`} />
          <Row label="Parents reached" value={fmtNum(x.parentsReached)} />
        </TabsContent>
        <TabsContent value="admin" className="mt-3 space-y-2">
          <Button size="sm" variant="outline" className="w-full"><Pencil className="mr-1 h-3 w-3" />Edit profile</Button>
          <Button size="sm" variant="outline" className="w-full"><Eye className="mr-1 h-3 w-3" />View ledger</Button>
          <Button size="sm" variant="destructive" className="w-full"><Power className="mr-1 h-3 w-3" />{m.lifecycle_status === "active" ? "Suspend merchant" : "Reactivate"}</Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ========================= shared ========================= */
function KpiSimple({ i: I, label, value, sub }: { i: any; label: string; value: string; sub?: string }) {
  return (
    <Card className="p-4 shadow-card">
      <I className="h-4 w-4 text-muted-foreground" />
      <div className="mt-2 font-display text-2xl">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}
function Stat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" | "danger" }) {
  const tones = { ok: "text-success", warn: "text-amber-600", danger: "text-destructive" };
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-lg ${tone ? tones[tone] : ""}`}>{value}</div>
    </div>
  );
}
function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between border-b pb-2 text-sm last:border-0"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>;
}
